import { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, RefreshCw } from 'lucide-react';
import { getDedupReview, resolveDedupReview, type DedupReviewGroup } from '../services/apiService';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';

function formatAmount(n: number): string {
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${sign}BRL ${abs}`;
}

export default function DedupReviewPage() {
  const [groups, setGroups] = useState<DedupReviewGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [resolvingFp, setResolvingFp] = useState<string | null>(null);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getDedupReview();
      setGroups(res.pending);
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Erro ao carregar fila de revisão');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleDistinct = async (fingerprint: string) => {
    setResolvingFp(fingerprint);
    try {
      await resolveDedupReview(fingerprint, 'distinct');
      // Reload to reflect resolved state
      await load();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Erro ao marcar como distintos');
    } finally {
      setResolvingFp(null);
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Revisão de Duplicatas</h1>
          <p className="text-sm text-gray-500 mt-1">
            Lançamentos com mesmo fingerprint (data + valor + banco) que ainda não foram resolvidos.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Atualizar
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-800 rounded-lg text-sm">
          {error}
        </div>
      )}

      {loading && groups.length === 0 && (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      )}

      {!loading && groups.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-500">
          <CheckCircle2 className="h-10 w-10 mx-auto mb-3 text-green-500" />
          <p>Nenhuma duplicata pendente.</p>
        </div>
      )}

      {groups.map((g) => (
        <div key={g.fingerprint} className="bg-white rounded-xl border border-amber-300 p-5 mb-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              <h2 className="font-semibold text-gray-900">
                Fingerprint: <code className="text-sm bg-amber-50 px-1.5 py-0.5 rounded">{g.fingerprint}</code>
              </h2>
              <span className="text-sm text-gray-500">({g.entries.length} lançamentos)</span>
            </div>
            <button
              onClick={() => handleDistinct(g.fingerprint)}
              disabled={resolvingFp === g.fingerprint}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {resolvingFp === g.fingerprint ? 'Salvando…' : 'São diferentes'}
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Data</th>
                  <th className="px-3 py-2 text-left font-medium">Descrição</th>
                  <th className="px-3 py-2 text-left font-medium">Conta</th>
                  <th className="px-3 py-2 text-right font-medium">Valor</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {g.entries.map((e, i) => (
                  <tr key={i} className="hover:bg-blue-50">
                    <td className="px-3 py-2 whitespace-nowrap">{e.date}</td>
                    <td className="px-3 py-2 text-gray-900">{e.payee}</td>
                    <td className="px-3 py-2 text-gray-700">{e.account}</td>
                    <td className={`px-3 py-2 text-right font-mono whitespace-nowrap ${e.amount < 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {formatAmount(e.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-500 mt-3">
            Se um dos lançamentos é redundante, abra o arquivo <code>.ledger</code> no editor e
            apague-o manualmente — a página detecta a remoção automaticamente.
          </p>
        </div>
      ))}
    </div>
  );
}
